import { apiError } from "../../../../../../utils/formErrors";
import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import FormField from '../../../../../../component/Formfield';
import {
  SettingsDivider,
  SettingsField,
  SettingsSaveBar,
  SettingsSection,
  SettingsToggle,
  useSettingsForm,
} from '../../../../../../component/settings';
import { getTimesheetSettings, updateTimesheetSettings } from '../../../../../../services/timesheet';
import { useNotify } from '../../../../../../component/NotificationProvider';

/**
 * Time log settings, in the book's settings shape: one column of fields, each
 * consequence written under its control, and a save bar that appears only once
 * something has changed.
 *
 * These fields were previously laid out in a two-column grid, which is the one
 * arrangement the book rules out by name — beside each other, "weekly regular
 * hours" and "max hours per day" are easy to set the wrong way round.
 */
const DEFAULTS = {
  weeklyRegularHours: 40,
  maxHoursPerDay: 24,
  requireProject: false,
  blockFutureDates: true,
  submitWindowDays: 0,
};

const Timelog = () => {
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { form, set, load, discard, dirtyCount } = useSettingsForm(DEFAULTS);

  useEffect(() => {
    (async () => {
      try {
        const res = await getTimesheetSettings();
        const d = res?.data;
        if (d) load({
          weeklyRegularHours: d.weeklyRegularHours ?? 40,
          maxHoursPerDay: d.maxHoursPerDay ?? 24,
          requireProject: !!d.requireProject,
          blockFutureDates: d.blockFutureDates !== false,
          submitWindowDays: d.submitWindowDays ?? 0,
        });
      } catch (e) { notify.error(e?.message || "Failed to load settings"); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const num = (v) => (v === "" ? "" : Number(v));

  const save = async () => {
    setSaving(true);
    const payload = {
      weeklyRegularHours: Number(form.weeklyRegularHours) || 0,
      maxHoursPerDay: Number(form.maxHoursPerDay) || 0,
      requireProject: !!form.requireProject,
      blockFutureDates: !!form.blockFutureDates,
      submitWindowDays: Number(form.submitWindowDays) || 0,
    };
    try {
      const res = await updateTimesheetSettings(payload);
      if (res?.success === false) throw apiError(res);
      // Re-baseline on what was actually sent, so a value the server coerced
      // ("" becoming 0) does not leave the save bar open on a phantom change.
      load(payload);
      notify.success("Timesheet settings saved");
    } catch (e) { notify.error(e?.message || "Could not save settings"); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        {[0, 1, 2].map((i) => (
          <Box key={i} className="pl-skeleton" sx={{ height: "2.75rem", borderRadius: "8px" }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      <SettingsSection
        title="Hours and overtime"
        description="The weekly threshold and the per-day cap used when totalling a timesheet."
      >
        <SettingsField
          label="Weekly regular hours"
          help="Hours beyond this in a week count as overtime."
        >
          <FormField
            type="text"
            noMargin
            height="2.75rem"
            value={form.weeklyRegularHours}
            onChange={(e) => set('weeklyRegularHours', num(e.target.value))}
          />
        </SettingsField>

        <SettingsField
          label="Max hours per day"
          help="An entry above this for a single day is rejected."
        >
          <FormField
            type="text"
            noMargin
            height="2.75rem"
            value={form.maxHoursPerDay}
            onChange={(e) => set('maxHoursPerDay', num(e.target.value))}
          />
        </SettingsField>
      </SettingsSection>

      <SettingsSection
        title="Submission rules"
        description="What people must provide, and which dates they can log against."
      >
        <SettingsField
          label="Submission window"
          help="How many days back a timesheet may still be submitted. 0 means no limit."
        >
          <FormField
            type="text"
            noMargin
            height="2.75rem"
            value={form.submitWindowDays}
            onChange={(e) => set('submitWindowDays', num(e.target.value))}
          />
        </SettingsField>

        <SettingsDivider />

        <SettingsToggle
          title="Require a project on every entry"
          description="Time must be linked to a project before the sheet can be submitted."
          checked={form.requireProject}
          onChange={(e) => set('requireProject', e.target.checked)}
        />
        <SettingsToggle
          title="Block future dates"
          description="Stops anyone logging time against a day that has not happened."
          checked={form.blockFutureDates}
          onChange={(e) => set('blockFutureDates', e.target.checked)}
        />
      </SettingsSection>

      <SettingsSaveBar
        dirtyCount={dirtyCount}
        onDiscard={discard}
        onSave={save}
        saving={saving}
      />
    </Box>
  );
};

export default Timelog;
